import React from "react";
import type { BillingTier } from "@/packages/domains/billing";
import { useBilling } from "@/packages/features-billing";
import { Tooltip } from "@/components/ui";

const TIER_ORDER: Record<BillingTier, number> = {
  free: 0,
  pro: 1,
  enterprise: 2,
};

interface UpgradePromptProps {
  /** Human-readable feature name, e.g. "unlimited audits" */
  feature: string;
  /** Minimum tier required to access the feature */
  requiredTier: BillingTier;
  /** Whether the gate is active (true = block the action) */
  enabled?: boolean;
  children: React.ReactNode;
}

/**
 * Wraps an action button and disables it with a tooltip when the workspace
 * tier is below the required tier AND `enabled` is true.
 *
 * When not locked, renders children unmodified.
 */
export function UpgradePrompt({
  feature,
  requiredTier,
  enabled = true,
  children,
}: UpgradePromptProps) {
  const { tier } = useBilling();
  const isLocked = enabled && TIER_ORDER[tier] < TIER_ORDER[requiredTier];

  if (!isLocked) return <>{children}</>;

  return (
    <Tooltip content={`Upgrade to ${requiredTier} to access ${feature}`}>
      {/* Wrap in span so tooltip works on disabled buttons (native disabled buttons don't fire mouse events) */}
      <span className="inline-block">
        {React.cloneElement(
          children as React.ReactElement<{ disabled?: boolean }>,
          { disabled: true },
        )}
      </span>
    </Tooltip>
  );
}
